/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

export const RecordingChangeEventType = "klava.call.recordingChange";
const RECORDING_STATE_KEY_MARKER = "_klava.call.recordingChange_";

export type RecordingChangeAction = "start" | "stop";

export type RecordingChangeType = "local";

export interface RecordingChangeEventContent {
  action: RecordingChangeAction;
  type: RecordingChangeType;
  callId: string;
  egressId?: string | null;
  deviceId?: string | null;
}

/**
 * Собирает state_key для состояния локальной записи конкретного устройства в звонке.
 */
export function makeRecordingStateKey(
  userId: string,
  deviceId: string,
  callId: string,
): string {
  return `${userId}_${deviceId}${RECORDING_STATE_KEY_MARKER}${callId}`;
}

/**
 * Извлекает device_id из state_key события локальной записи.
 */
export function parseRecordingStateKeyDeviceId(
  sender: string | null | undefined,
  stateKey: string | null | undefined,
): string | null {
  if (!sender || !stateKey) {
    return null;
  }

  const senderPrefix = `${sender}_`;
  if (!stateKey.startsWith(senderPrefix)) {
    return null;
  }

  const markerIndex = stateKey.lastIndexOf(RECORDING_STATE_KEY_MARKER);
  if (markerIndex <= senderPrefix.length) {
    return null;
  }

  const deviceId = stateKey.slice(senderPrefix.length, markerIndex);
  return deviceId.length > 0 ? deviceId : null;
}

/**
 * Извлекает callId из state_key события локальной записи.
 */
export function parseRecordingStateKeyCallId(
  stateKey: string | null | undefined,
): string | null {
  if (!stateKey) {
    return null;
  }

  const markerIndex = stateKey.lastIndexOf(RECORDING_STATE_KEY_MARKER);
  if (markerIndex < 0) {
    return null;
  }

  const callId = stateKey.slice(
    markerIndex + RECORDING_STATE_KEY_MARKER.length,
  );
  return callId.length > 0 ? callId : null;
}

/**
 * Проверка входящих данных события записи.
 */
export function isRecordingChangeEventContent(
  content: unknown,
): content is RecordingChangeEventContent {
  if (typeof content !== "object" || content === null) {
    return false;
  }

  const candidate = content as Partial<RecordingChangeEventContent>;
  return (
    (candidate.action === "start" || candidate.action === "stop") &&
    candidate.type === "local" &&
    typeof candidate.callId === "string" &&
    candidate.callId.length > 0 &&
    (candidate.deviceId === undefined ||
      candidate.deviceId === null ||
      typeof candidate.deviceId === "string")
  );
}
